import { HTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import type { LucideIcon } from 'lucide-react';

interface NavDropdownItemProps extends HTMLAttributes<HTMLAnchorElement> {
  href: string;
  icon?: LucideIcon;
  title: string;
  subtitle?: string;
}

export const NavDropdownItem = ({
  href,
  icon: IconComponent,
  title,
  subtitle,
  className,
  ...props
}: NavDropdownItemProps) => {
  const navigate = useNavigate();
  // Check if the link is external
  const isExternalLink = href.startsWith('http://') || href.startsWith('https://');

  const handleNavigation = () => {
    window.scrollTo(0, 0);
    navigate(href);
  };

  const content = (
    <>
      {IconComponent && (
        <div className="mt-1 rounded-lg bg-primary-50 p-2 transition-colors group-hover:bg-primary-100">
          <IconComponent className="h-5 w-5 text-primary-600" />
        </div>
      )}
      <div>
        <div className="font-medium text-gray-900">{title}</div>
        {subtitle && <div className="mt-0.5 text-sm text-gray-500">{subtitle}</div>}
      </div>
    </>
  );

  // Use appropriate component based on link type
  return isExternalLink ? (
    <a
      href={href}
      className={cn(
        'group flex items-start gap-4 rounded-lg p-3 transition-colors hover:bg-gray-50',
        className
      )}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {content}
    </a>
  ) : (
    <span
      onClick={handleNavigation}
      className={cn(
        'group flex items-start gap-4 rounded-lg p-3 transition-colors hover:bg-gray-50 cursor-pointer',
        className
      )}
      {...props}
    >
      {content}
    </span>
  );
};